<template>
  <div class="news-export">
    <div class="export-header">
      <h3 class="export-title">
        <i class="fas fa-file-export"></i>
        Exportar Noticias
      </h3>
      <button @click="$emit('close')" class="close-button" title="Cerrar">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="export-body">
      <div class="export-info">
        <p>Exporta todas las noticias en formato JSON o CSV para respaldo o migración.</p>
        <div class="news-count">
          <i class="fas fa-newspaper"></i>
          <span>{{ news.length }} noticia{{ news.length !== 1 ? 's' : '' }} disponible{{ news.length !== 1 ? 's' : '' }}</span>
        </div>
      </div>

      <div class="export-options">
        <button @click="exportToJSON" class="export-option" :disabled="news.length === 0">
          <div class="option-icon json">
            <i class="fas fa-file-code"></i>
          </div>
          <div class="option-content">
            <div class="option-title">Exportar a JSON</div>
            <div class="option-description">Formato estándar para respaldo y migración</div>
          </div>
          <div class="option-arrow">
            <i class="fas fa-download"></i>
          </div>
        </button>

        <button @click="exportToCSV" class="export-option" :disabled="news.length === 0">
          <div class="option-icon csv">
            <i class="fas fa-file-csv"></i>
          </div>
          <div class="option-content">
            <div class="option-title">Exportar a CSV</div>
            <div class="option-description">Compatible con Excel y hojas de cálculo</div>
          </div>
          <div class="option-arrow">
            <i class="fas fa-download"></i>
          </div>
        </button>
      </div>

      <div v-if="news.length === 0" class="empty-state">
        <i class="fas fa-inbox"></i>
        <p>No hay noticias para exportar</p>
      </div>

      <div v-if="exported" class="export-success">
        <i class="fas fa-check-circle"></i>
        <p>Archivo descargado correctamente</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { NewsItem } from '@/composables/useNews';

interface Props {
  news: NewsItem[];
}

const props = defineProps<Props>();
defineEmits<{
  close: []
}>();

const exported = ref(false);

const exportToJSON = () => {
  const data = props.news.map(item => ({
    title: item.title,
    content: item.content,
    createdAt: item.createdAt.toISOString(),
    author: item.author
  }));

  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  downloadFile(blob, `noticias-${getFormattedDate()}.json`);

  exported.value = true;
  setTimeout(() => {
    exported.value = false;
  }, 3000);
};

const exportToCSV = () => {
  // CSV Header
  let csv = 'title,content,createdAt,author\n';

  // CSV Rows
  props.news.forEach(item => {
    const title = escapeCSV(item.title);
    const content = escapeCSV(item.content);
    const createdAt = item.createdAt.toISOString();
    const author = escapeCSV(item.author);

    csv += `"${title}","${content}","${createdAt}","${author}"\n`;
  });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadFile(blob, `noticias-${getFormattedDate()}.csv`);

  exported.value = true;
  setTimeout(() => {
    exported.value = false;
  }, 3000);
};

const escapeCSV = (str: string): string => {
  return str.replace(/"/g, '""');
};

const getFormattedDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
};

const downloadFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
</script>

<style scoped>
.news-export {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.export-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-neutral-200);
  flex-shrink: 0;
}

.export-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
}

.export-title i {
  color: var(--color-primary-600);
}

.close-button {
  background: none;
  border: none;
  color: var(--color-neutral-500);
  font-size: var(--font-size-xl);
  cursor: pointer;
  transition: var(--transition-all);
  padding: var(--spacing-2);
  border-radius: var(--border-radius-md);
}

.close-button:hover {
  background: var(--color-neutral-100);
  color: var(--color-neutral-700);
}

.export-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-6);
}

.export-info {
  margin-bottom: var(--spacing-6);
}

.export-info p {
  color: var(--color-neutral-600);
  margin: 0 0 var(--spacing-4) 0;
}

.news-count {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-4);
  background: var(--color-primary-50);
  border: 1px solid var(--color-primary-200);
  border-radius: var(--border-radius-full);
  color: var(--color-primary-700);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
}

.export-options {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}

.export-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  padding: var(--spacing-5);
  background: white;
  border: 2px solid var(--color-neutral-200);
  border-radius: var(--border-radius-xl);
  cursor: pointer;
  transition: var(--transition-all);
  text-align: left;
  width: 100%;
}

.export-option:hover:not(:disabled) {
  border-color: var(--color-primary-300);
  background: var(--color-primary-50);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.export-option:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.option-icon.json {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
}

.option-icon.csv {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #047857;
}

.option-content {
  flex: 1;
  min-width: 0;
}

.option-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin-bottom: var(--spacing-1);
}

.option-description {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
}

.option-arrow {
  color: var(--color-neutral-400);
  font-size: var(--font-size-lg);
  flex-shrink: 0;
  transition: var(--transition-all);
}

.export-option:hover:not(:disabled) .option-arrow {
  color: var(--color-primary-600);
  transform: translateX(4px);
}

.empty-state {
  text-align: center;
  padding: var(--spacing-12) var(--spacing-6);
  color: var(--color-neutral-500);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: var(--spacing-4);
  display: block;
  color: var(--color-neutral-300);
}

.empty-state p {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.export-success {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-3);
  padding: var(--spacing-4);
  background: var(--color-success-50);
  border: 1px solid var(--color-success-200);
  border-radius: var(--border-radius-lg);
  color: var(--color-success-700);
  font-weight: var(--font-weight-medium);
  margin-top: var(--spacing-4);
  animation: slideIn 0.3s ease;
}

.export-success i {
  font-size: var(--font-size-xl);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 640px) {
  .export-header,
  .export-body {
    padding: var(--spacing-4);
  }

  .export-option {
    padding: var(--spacing-4);
  }

  .option-icon {
    width: 48px;
    height: 48px;
    font-size: 1.25rem;
  }
}
</style>
